import React, { useEffect, useState } from "react";
import { CircularProgress, Dialog } from "@material-ui/core";
import { btn, input, tone } from "./pricePlanStyles";

/**
 * Hộp xác nhận "gõ đúng chữ" (APPLY / ROLLBACK) dùng cho Apply và Roll back.
 * - Nút xác nhận chỉ bật khi gõ đúng chữ + tick đủ các ô bắt buộc.
 * - Trong lúc chạy: khoá mọi nút (chống bấm 2 lần), không cho đóng hộp.
 * - Xong: hiện `result` (hoặc `error`) và chỉ còn nút Close.
 */
export interface ConfirmCheckbox {
	id: string;
	label: string;
}

interface Props {
	open: boolean;
	title: string;
	/** Nội dung mô tả (đếm số trạm…) */
	children?: React.ReactNode;
	word: string;
	confirmLabel: string;
	checkboxes?: ConfirmCheckbox[];
	busy: boolean;
	result?: React.ReactNode | null;
	error?: string | null;
	onConfirm: () => void;
	onClose: () => void;
}

export default function ConfirmTypeDialog(props: Props) {
	const {
		open,
		title,
		children,
		word,
		confirmLabel,
		checkboxes = [],
		busy,
		result,
		error,
		onConfirm,
		onClose,
	} = props;
	const [typed, setTyped] = useState("");
	const [checked, setChecked] = useState<{ [id: string]: boolean }>({});

	// Mở lại hộp ⇒ xoá chữ đã gõ / ô đã tick
	useEffect(() => {
		if (open) {
			setTyped("");
			setChecked({});
		}
	}, [open]);

	const allChecked = checkboxes.every((c) => checked[c.id]);
	const canConfirm = typed === word && allChecked && !busy && !result;
	const done = !!result;

	const close = () => {
		if (!busy) onClose();
	};

	return (
		<Dialog
			open={open}
			onClose={close}
			maxWidth="sm"
			fullWidth
			disableEscapeKeyDown={busy}>
			<div
				style={{ padding: "18px 20px" }}
				data-testid="confirm-type-dialog">
				<h3 style={{ margin: "0 0 12px", fontSize: 18 }}>{title}</h3>
				{done ? (
					<div data-testid="confirm-result">{result}</div>
				) : (
					<>
						{children}
						{checkboxes.map((c) => (
							<label
								key={c.id}
								style={{
									display: "flex",
									gap: 8,
									alignItems: "flex-start",
									margin: "12px 0 0",
									cursor: "pointer",
								}}>
								<input
									type="checkbox"
									checked={!!checked[c.id]}
									disabled={busy}
									onChange={(e) => {
										// đọc giá trị NGAY — trong updater React đã trả checked về giá trị cũ
										const v = e.target.checked;
										setChecked((prev) => ({ ...prev, [c.id]: v }));
									}}
								/>
								<span style={{ fontWeight: 600, color: tone.err }}>{c.label}</span>
							</label>
						))}
						<label style={{ display: "block", margin: "14px 0 6px", fontSize: 14 }}>
							Type <b style={{ fontFamily: "ui-monospace, Menlo, monospace" }}>{word}</b> to confirm
						</label>
						<input
							aria-label={`Type ${word} to confirm`}
							value={typed}
							disabled={busy}
							autoComplete="off"
							spellCheck={false}
							onChange={(e) => setTyped(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter" && canConfirm) onConfirm();
							}}
							style={{ ...input, width: "100%" }}
						/>
						{error ? (
							<p
								role="alert"
								style={{ color: tone.err, fontWeight: 600, margin: "12px 0 0" }}>
								{error}
							</p>
						) : null}
					</>
				)}
				<div
					style={{
						display: "flex",
						justifyContent: "flex-end",
						gap: 8,
						marginTop: 18,
						alignItems: "center",
					}}>
					{busy ? <CircularProgress size={20} /> : null}
					<button
						type="button"
						onClick={close}
						disabled={busy}
						style={btn(false, busy)}>
						{done ? "Close" : "Cancel"}
					</button>
					{!done ? (
						<button
							type="button"
							onClick={() => canConfirm && onConfirm()}
							disabled={!canConfirm}
							style={btn(true, !canConfirm)}>
							{busy ? "Working…" : confirmLabel}
						</button>
					) : null}
				</div>
			</div>
		</Dialog>
	);
}
